import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { getTokenClaims } from '@/lib/admin';
import { Button } from '@/components/Button';
import { Card, CardSub, CardTitle } from '@/components/Card';
import { ShieldCheck, ShieldAlert, RefreshCw } from 'lucide-react';

// Diagnostic page: confirms admin claim + Firestore role + raw ID token
// claims. Useful when "I ran seed:admin but the admin link isn't showing"
// — usually because the user hasn't refreshed their ID token yet.
//
// Reachable at /admin/verify. Anyone signed in (verified or not) can view
// their own status — it's a self-diagnostic, not sensitive data.

export function AdminVerify() {
  const {
    user,
    profile,
    isAdmin,
    profileIsAdmin,
    tokenIsAdmin: authTokenIsAdmin,
    adminClaimError,
    refreshAdminClaim,
  } = useAuth();
  const [tokenIsAdmin, setTokenIsAdmin] = useState<boolean | null>(null);
  const [claims, setClaims] = useState<Record<string, unknown>>({});
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  async function load() {
    if (!user) return;
    const c = await getTokenClaims();
    setTokenIsAdmin(c.admin === true);
    setClaims(c);
  }

  useEffect(() => {
    load();
    // Re-load when user identity changes
  }, [user]);

  useEffect(() => {
    if (authTokenIsAdmin !== null) setTokenIsAdmin(authTokenIsAdmin);
  }, [authTokenIsAdmin]);

  async function onRefresh() {
    setRefreshError(null);
    setRefreshing(true);
    try {
      await refreshAdminClaim(true);
      await load();
    } catch (e) {
      setRefreshError((e as Error).message ?? 'Could not refresh token.');
    } finally {
      setRefreshing(false);
    }
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card>
          <p className="text-sm">Sign in to view your admin status.</p>
          <div className="mt-3">
            <Link to="/signin">
              <Button>Sign in</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950 text-stone-900 dark:text-stone-100">
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        <header>
          <h1 className="text-2xl font-bold">Admin status</h1>
          <p className="text-sm text-stone-600 dark:text-stone-400 mt-1">
            Diagnostic view of your auth token + profile role.
          </p>
        </header>

        <Card>
          <CardTitle>You</CardTitle>
          <dl className="text-sm space-y-1.5">
            <Row label="Email" value={user.email ?? '(none)'} />
            <Row label="UID" value={user.uid} />
            <Row label="Email verified" value={user.emailVerified ? 'Yes' : 'No'} />
            <Row
              label="Anonymous"
              value={user.isAnonymous ? 'Yes (anon trial)' : 'No'}
            />
          </dl>
        </Card>

        <Card>
          <CardTitle>Admin checks</CardTitle>
          <dl className="text-sm space-y-2">
            <Row
              label="ID token `admin` claim"
              value={
                tokenIsAdmin === null ? 'checking…' : tokenIsAdmin ? '✓ true' : '✗ false'
              }
              tone={tokenIsAdmin === true ? 'good' : tokenIsAdmin === false ? 'bad' : undefined}
            />
            <Row
              label="Firestore profile role"
              value={profile?.role ?? '(no profile yet)'}
              tone={profile?.role === 'admin' ? 'good' : profile ? 'bad' : undefined}
            />
            <Row
              label="useAuth().isAdmin (backend-aligned)"
              value={isAdmin ? '✓ true' : '✗ false'}
              tone={isAdmin ? 'good' : 'bad'}
            />
          </dl>
          <CardSub className="mt-3 text-xs">
            The admin route and backend both use the ID token claim. The
            Firestore role is diagnostic context and may update before the
            token claim refreshes.
          </CardSub>
          <div className="mt-4 flex gap-2">
            <Button size="sm" onClick={onRefresh} loading={refreshing}>
              <RefreshCw className="h-4 w-4 mr-1.5" />
              Refresh ID token
            </Button>
          </div>
          {refreshError && (
            <p className="mt-2 text-sm text-rose-600 dark:text-rose-400">{refreshError}</p>
          )}
          {adminClaimError && (
            <p className="mt-2 text-sm text-rose-600 dark:text-rose-400">{adminClaimError}</p>
          )}
        </Card>

        {tokenIsAdmin === false && (
          <Card>
            <CardTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-600" />
              {profileIsAdmin ? 'Admin token needs refresh' : 'Not admin yet?'}
            </CardTitle>
            <CardSub>
              {profileIsAdmin
                ? 'Your Firestore profile says admin, but the token claim used by the backend is not active yet.'
                : 'To grant admin to this account, run on your machine:'}
            </CardSub>
            {!profileIsAdmin && (
            <pre className="mt-2 text-xs p-3 rounded-lg bg-stone-100 dark:bg-stone-800 overflow-x-auto">
{`cd scripts
GOOGLE_APPLICATION_CREDENTIALS=~/.firebase-keys/practicecom-9f7b3-admin.json \\
  npm run seed:admin -- --email ${user.email}`}
            </pre>
            )}
            <p className="mt-2 text-xs text-stone-500">
              Click "Refresh ID token" above, or sign out and back in.
            </p>
          </Card>
        )}

        {tokenIsAdmin === true && (
          <Card>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              You are an admin.
            </CardTitle>
            <CardSub>
              All admin callables and routes are unlocked. <Link to="/admin" className="text-brand-700 dark:text-brand-400 font-medium">Go to admin dashboard →</Link>
            </CardSub>
          </Card>
        )}

        <Card>
          <CardTitle>Raw ID token claims</CardTitle>
          <CardSub className="mb-2">Useful when filing a bug.</CardSub>
          <pre className="text-xs p-3 rounded-lg bg-stone-100 dark:bg-stone-800 overflow-x-auto">
{JSON.stringify(claims, null, 2)}
          </pre>
        </Card>
      </main>
    </div>
  );
}

function Row({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'good' | 'bad';
}) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-stone-600 dark:text-stone-400">{label}</dt>
      <dd
        className={
          tone === 'good'
            ? 'text-emerald-700 dark:text-emerald-400 font-medium'
            : tone === 'bad'
              ? 'text-rose-700 dark:text-rose-400 font-medium'
              : 'text-stone-900 dark:text-stone-100'
        }
      >
        {value}
      </dd>
    </div>
  );
}
